<template>
    <div>
        <rkm-modal
            extra-large
            ref="RkmModal"
            title="Intervenções e/ou procedimentos clínicos realizados"
        >
            <rkm-form
                ref="RkmForm"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="5">
                        <rkm-form-input-autocomplete
                            :disabled="!!item"
                            :repository="repositories.procedimento"
                            :request-query-string-params="requestQueryStringParams.procedimento"
                            :search-fields="fields.procedimento"
                            :text-field="(item) => `${formatProcedimento(item.codigo)} - ${item.nome}`"
                            :validations="validations.procedimento"
                            @select="model.cid = null"
                            label="Procedimento"
                            v-model="model.procedimento"
                        />
                    </rkm-col>

                    <rkm-col cols="5">
                        <rkm-form-input-autocomplete
                            :disabled="!model.procedimento"
                            :repository="repositories.cid"
                            :request-query-string-params="requestQueryStringParams.cid"
                            :search-fields="fields.cid"
                            :text-field="(item) => `${formatCid(item.codigo)} - ${item.nome}`"
                            :validations="validations.cid"
                            label="CID"
                            v-model="model.cid"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :validations="validations.quantidade"
                            label="Quantidade"
                            number
                            v-model="model.quantidade"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-textarea
                            :validations="validations.observacao"
                            label="Observações"
                            rows="4"
                            v-model="model.observacao"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <rkm-form v-if="model.material"
                ref="RkmFormMateriais"
            >

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.material"
                            :request-query-string-params="requestQueryStringParams.material"
                            :search-fields="fields.material"
                            :text-field="(item) => item.descricaoCompleta"
                            label="Material"
                            v-model="model.material.material"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="model.material.estoque"
                            disabled
                            label="Estoque"
                        >
                            <rkm-input-group-append slot="append">
                                <rkm-button
                                    :disabled="!model.material.material"
                                    @click="loadEstoque"
                                    color="primary"
                                    title="Atualizar estoque"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="sync-alt" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input>
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :validations="validations.material.quantidade"
                            label="Quantidade"
                            type="number"
                            v-model="model.material.quantidade"
                        >
                            <rkm-input-group-append
                                slot="append"
                            >
                                <rkm-button
                                    :disabled="!quantidadeValida"
                                    @click="$refs.ModalLote.open(model.material.entradaItens)"
                                    title="Visualizar lotes"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="box-open" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input>
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :disabled="!quantidadeValida"
                            :label="false"
                            @click="$refs.RkmFormMateriais.validate(editIndex !== null ? updateMaterial : add)"
                            color="primary"
                        >
                                {{ editIndex !== null ? 'Editar' : 'Incluir' }}
                        </rkm-button>
                    </rkm-col>

                </rkm-row>

                <rkm-area title="Materiais">
                        <rkm-table
                            :fields="fields.materiais"
                            :items="model.materiais"
                            hide-filters
                            hide-pagination
                        >

                            <template v-slot:cell(actions)="{item, index}">

                                <rkm-button
                                    @click="editMaterial(item, index)"
                                    color="primary"
                                    small
                                    title="Editar"
                                >
                                    <rkm-icon icon="pen" />
                                </rkm-button>

                                <rkm-button
                                    @click="destroy(index)"
                                    color="danger"
                                    small
                                    title="Excluir"
                                >
                                    <rkm-icon icon="trash" />
                                </rkm-button>
                            </template>
                        </rkm-table>
                </rkm-area>
            </rkm-form>

            <template v-slot:footer="{cancel}">
                <rkm-button @click="cancel">
                    Fechar
                </rkm-button>

                <rkm-button
                    @click="save"
                    color="primary"
                >
                    {{item ? 'Salvar' : 'Incluir'}}
                </rkm-button>
            </template>
        </rkm-modal>

        <ModalLote
            :model="model.material"
            ref="ModalLote"
        />

    </div>
</template>

<script>

// Components.
import ModalLote from '@/components/almoxarifado/components/lote/Modal'

// Enums.
import procedimento from '@/enums/sigtap/procedimento'
import procedimentoAtencaoBasica from '@/enums/atencao-basica/atendimento-odontologico/procedimento'

// Mixins.
import {formats, object, repository} from '@/mixins'

//Vuex
import {mapActions} from 'vuex'

export default {
    components: {ModalLote},
    computed: {
        requestQueryStringParams() {
            return {
                procedimento: {
                    cbo_lotacao_atual: true,
                    paciente_id: this.data.additionals.paciente.id,
                    competencia_id: this.data.additionals.isRetroativo ? this.data.additionals.agendamentoCompetenciaId : null,
                    where_not: {
                        codigo: [
                            this.enums.procedimento.AVALIACAO_ANTROPOMETRICA,
                            this.enums.procedimento.GLICEMIA_CAPILAR,
                            this.enums.procedimento.MEDICAO_DE_ALTURA,
                            this.enums.procedimento.MEDICAO_DE_PESO,
                            this.enums.procedimento.AFERICAO_DE_TEMPERATURA,
                            this.enums.procedimento.AFERICAO_PRESSAO_ARTERIAL,
                        ],
                    },
                },
                cid: this.model.procedimento && this.model.procedimento.hasCidPrincipal
                    ? {
                        principal: true,
                        procedimento_id: this.model.procedimento.id,
                    }
                    : {}
            }
        },
        validations() {
            return {
                procedimento: {required: true},
                cid: {required: this.model.procedimento && !!this.model.procedimento.hasCidPrincipal},
                quantidade: {
                    required: true,
                    min_value: 1,
                    max_value: this.data.additionals.isAtendimentoSaudeBucal ? this.getMaxValue() : 3,
                },
                material: {
                    quantidade: {
                        required: !!this.model.material.material,
                        min_value: 1,
                        max_value: this.model.material.estoque,
                    },
                },
                observacao: {max: 500},
            }
        },
        quantidadeValida() {
            const quantidade = parseInt(this.model.material.quantidade)

            return quantidade > 0 && quantidade <= this.model.material.estoque
        },
    },
    created() {
        this.repositories = {
            cid: this.getConectaSusRepository('sigtap.cid'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            material: this.getConectaSusRepository('cadastro.material'),
        }
    },
    data: () => ({
        enums: {procedimento,procedimentoAtencaoBasica},
        editIndex: null,
        fields: {
            procedimento: [
                {key: 'codigo', searchable: true},
                {key: 'nome', searchable: true},
            ],
            cid: [
                {key: 'codigo', searchable: true},
                {key: 'nome', searchable: true},
            ],
            material: [
                {key: "codigo_material", searchable: true},
                {key: "descricao", searchable: true},
            ],
            materiais: [
                {key: "descricaoCompleta", label: 'Material', searchable: true},
                {key: "quantidade", label: 'Quantidade', searchable: true},
                {class: 'col-min text-center', key: 'actions', label: 'Ações'},
            ],
        },
        form: {
            possuiEntradaComLote: false,
        },
        item: null,
        model: {
            procedimento: null,
            cid: null,
            quantidade: 1,
            observacao: null,
            material: {
                entradaItens: [],
                escolheuEntradaItemManualmente: null,
                estoque: null,
                material: null,
                quantidade: null,
            },
            materiais: [],
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(item = null) {
            this.item = item

            if (this.item) {
                this.edit()
            } else {
                this.create()
            }

            this.$refs.RkmModal.show()
        },
        create() {
            this.resetForm()
        },
        loadEstoque() {
            this.form.possuiEntradaComLote = false
            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = false
            this.model.material.estoque = 0
            this.model.material.quantidade = null

            this.setLoading()

            this.repositories.material.getEstoque(this.model.material.material.id)
                .then(({data}) => {
                    this.form.possuiEntradaComLote = data.possuiEntradaComLote
                    this.model.material.estoque = data.total
                    this.model.material.entradaItens = data.porEntrada.map(estoqueEntrada => {
                        estoqueEntrada.quantidadeSaida = 0
                        return estoqueEntrada
                    })
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        selectEntradaItens() {
            this.model.material.escolheuEntradaItemManualmente = false
            this.model.material.entradaItens.map(e => e.quantidadeSaida = 0)

            let quantidade = parseInt(this.model.material.quantidade)
            let i = 0

            while (quantidade > 0) {
                const entradaItem = this.model.material.entradaItens[i]
                const estoque = parseInt(entradaItem.estoque)

                if (quantidade > estoque) {
                    entradaItem.quantidadeSaida = estoque
                    quantidade -= estoque
                } else {
                    entradaItem.quantidadeSaida = quantidade
                    quantidade = 0
                }

                i++
            }
        },
        edit() {

            this.model = this.clone({
                procedimento: this.item.procedimento,
                cid: this.item.cid,
                quantidade: this.item.quantidade,
                observacao: this.item.observacao,
                material: {
                    entradaItens: [],
                    escolheuEntradaItemManualmente: null,
                    estoque: null,
                    material: null,
                    quantidade: null,
                },
                materiais: this.item.procedimento.materiais || [],
            })
        },
        store() {
            this.data.model.procedimentosClinicos.push({
                procedimento: {
                    id: this.model.procedimento.id,
                    codigo: this.model.procedimento.codigo,
                    nome: this.model.procedimento.nome,
                    hasInstrumentoRegistroRaas: !!this.model.procedimento.hasInstrumentoRegistroRaas,
                    hasCidPrincipal: this.model.procedimento.hasCidPrincipal,
                    materiais: this.model.materiais,
                },
                cid: this.model.cid ? {
                    id: this.model.cid.id,
                    codigo: this.model.cid.codigo,
                    nome: this.model.cid.nome,
                } : null,
                automatico: false,
                quantidade: this.model.quantidade,
                observacao: this.model.observacao,
            })
        },
        update() {
            Object.assign(this.item, {
                procedimento: {
                    id: this.model.procedimento.id,
                    codigo: this.model.procedimento.codigo,
                    nome: this.model.procedimento.nome,
                    hasInstrumentoRegistroRaas: !!this.model.procedimento.hasInstrumentoRegistroRaas,
                    hasCidPrincipal: this.model.procedimento.hasCidPrincipal,
                },
                cid: this.model.cid ? {
                    id: this.model.cid.id,
                    codigo: this.model.cid.codigo,
                    nome: this.model.cid.nome,
                } : null,
                automatico: false,
                quantidade: this.model.quantidade,
                observacao: this.model.observacao,
            })
        },
        add() {
            this.model.materiais.push({
                id: this.model.material.material.id,
                descricaoCompleta: this.model.material.material.descricaoCompleta,
                quantidade: this.model.material.quantidade,
                materialEscolheuEntradaItemManualmente: this.model.material.escolheuEntradaItemManualmente,
                materialEntradaItens: this.model.material.entradaItens,
            })

            this.clearMaterial()
        },
        editMaterial(material, index) {
            this.editIndex = index

            this.model.material.material = {
                id: material.id,
                descricaoCompleta: material.descricaoCompleta,
            }

            this.model.material.quantidade = material.quantidade
        },
        updateMaterial() {

            if (this.editIndex === null) return

            this.$set(this.model.materiais, this.editIndex, {
                id: this.model.material.material.id,
                descricaoCompleta: this.model.material.material.descricaoCompleta,
                quantidade: this.model.material.quantidade,
            })

            this.editIndex = null

            this.clearMaterial()
        },
        clearMaterial() {

            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = null
            this.model.material.estoque = null
            this.model.material.material = null
            this.model.material.quantidade = null

            this.$nextTick().then(() => this.$refs.RkmFormMateriais.resetValidator())
        },
        destroy(index) {
            this.showDeleteConfirmation('Deseja realmente excluir o material?', async () => {
                this.model.materiais.splice(index, 1)
            })
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    if (!this.item && this.data.model.procedimentosClinicos.find(procedimentoClinico => procedimentoClinico.procedimento.id === this.model.procedimento.id)) {
                        this.showValidationNotification('O procedimento clínico já foi incluído.')
                        return
                    }

                    if (this.model.procedimento.codigo === this.enums.procedimento.ESCUTA_INICIAL_ORIENTACAO_ACOLHIMENTO_A_DEMANDA_ESPONTANEA) {

                        let mensagemDemanda = '';

                        if(this.data.additionals.isDemandaEspontanea){

                            mensagemDemanda = `Este procedimento é registrado automaticamente pelo sistema, evitando inconsistências no registro de atendimentos de demanda espontânea.`
                        }else{

                            mensagemDemanda = `Não pode ser preenchido com o procedimento <strong>03.01.04.007-9 - Escuta inicial / orientação (acolhimento a demanda espontânea)</strong>.
                            Esta informação é registrada automaticamente pelo sistema para atendimentos de Demanda Espontânea.`

                        }

                        this.showValidationNotification(mensagemDemanda)
                        return
                    }

                    if (this.item) {
                        this.update()
                    } else {
                        this.store()
                    }

                    this.$refs.RkmModal.hide()
                })
        },
        resetForm() {
            this.model.procedimento = null
            this.model.cid = null
            this.model.quantidade = 1
            this.model.observacao = null

            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = null
            this.model.material.estoque = null
            this.model.material.material = null
            this.model.material.quantidade = null
            this.model.materiais = []
        },
        getMaxValue() {
            if(this.model.procedimento) {
                if([
                    this.enums.procedimentoAtencaoBasica.RADIOGRAFIA_OCLUSAL,
                    this.enums.procedimentoAtencaoBasica.COLOCACAO_PLACA_MORDIDA,
                    this.enums.procedimentoAtencaoBasica.MANUTENCAOO_CONSERTO_APARELHO,
                    this.enums.procedimentoAtencaoBasica.MOLDAGEM_DENTO_GENGIVAL,
                    this.enums.procedimentoAtencaoBasica.EXERESE_CISTO,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_CIRURGICO_FISTULA,
                    this.enums.procedimentoAtencaoBasica.CORRECAO_BRIDAS_MUSCULARES,
                    this.enums.procedimentoAtencaoBasica.CORRECAO_IRREGULARIDADES_REBORDO_ALVEOLAR,
                    this.enums.procedimentoAtencaoBasica.REMOCAO_TORUS_EXOSTOSES,
                    this.enums.procedimentoAtencaoBasica.CONTENCAOO_DENTES_SPLINTAGEM,
                    this.enums.procedimentoAtencaoBasica.REDUCAO_LUXACAOO_TEMPORO_MANDIBULAR,
                    this.enums.procedimentoAtencaoBasica.RETIRADA_MATERIAL_SÍNTESE,
                    this.enums.procedimentoAtencaoBasica.MANTENEDOR_ESPAÇO,
                    this.enums.procedimentoAtencaoBasica.PLACA_OCLUSAL,
                    this.enums.procedimentoAtencaoBasica.PLANO_INCLINADO,
                    this.enums.procedimentoAtencaoBasica.PROTESE_PARCIAL_MANDIBULAR_REMOVIVEL,
                    this.enums.procedimentoAtencaoBasica.PROTESE_PARCIAL_MAXILAR_REMOVIVEL,
                    this.enums.procedimentoAtencaoBasica.PROTESE_TEMPORARIA,
                    this.enums.procedimentoAtencaoBasica.PROTESE_TOTAL_MANDIBULAR,
                    this.enums.procedimentoAtencaoBasica.PROTESE_TOTAL_MAXILAR,
                    this.enums.procedimentoAtencaoBasica.APARELHO_FIXO_BILATERAL_PARA_FECHAMENTO_DE_DIASTEMA,
                    this.enums.procedimentoAtencaoBasica.APARELHO_ORTODONTICO_FIXO,
                    this.enums.procedimentoAtencaoBasica.APARELHO_ORTOPEDICO_E_ORTODONTICO_REMOVIVEL,
                    this.enums.procedimentoAtencaoBasica.APARELHO_ORTOPEDICO_FIXO,
                    this.enums.procedimentoAtencaoBasica.APARELHO_PARA_BLOQUEIO_MAXILO_MANDIBULAR,
                ].includes(this.model.procedimento.codigo)) {
                    return 2
                }
                if([
                    this.enums.procedimentoAtencaoBasica.APLICACAO_CARIOSTATICO,
                    this.enums.procedimentoAtencaoBasica.APLICACAO_SELANTE,
                    this.enums.procedimentoAtencaoBasica.SELAMENTO_PROVISORIO_CAVIDADE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.RADIOGRAFIA_INTERPROXIMAL,
                    this.enums.procedimentoAtencaoBasica.RADIOGRAFIA_PERIAPICAL,
                    this.enums.procedimentoAtencaoBasica.CAPEAMENTO_PULPAR,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_PERMANENTE_ANTERIOR,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_INICIAL_DENTE_TRAUMATIZADO,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_RESTAURADOR_ATRAUMATICO,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTEDECIDUO_POSTERIOR_RESINA_COMPOSTA,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_DECIDUO_POSTERIOR_AMALGAMA,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_DECIDUO_POSTERIOR_IONOMERO_VIDRO,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_DECIDUO_ANTERIOR_RESINA_COMPOSTA,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_PERMANENTE_POSTERIOR_RESINA_COMPOSTA,
                    this.enums.procedimentoAtencaoBasica.RESTAURACAO_DENTE_PERMANENTE_POSTERIOR_AMALGAMA,
                    this.enums.procedimentoAtencaoBasica.ACESSO_POLPA_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.CURATIVO_DEMORA,
                    this.enums.procedimentoAtencaoBasica.OBTURACAO_DENTE_DECIDUO,
                    this.enums.procedimentoAtencaoBasica.OBTURACAO_DENTE_PERMANENTE_BIRRADICULAR,
                    this.enums.procedimentoAtencaoBasica.OBTURACAO_DENTE_PERMANENTE_RAIZES,
                    this.enums.procedimentoAtencaoBasica.OBTURACAO_DENTE_PERMANENTE_UNIRRADICULAR,
                    this.enums.procedimentoAtencaoBasica.PULPOTOMIA_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.RETRATAMENTO_ENDODÔNTICO_DENTE_PERMANENTE_BIRADICULAR,
                    this.enums.procedimentoAtencaoBasica.RETRATAMENTO_ENDODÔNTICO_DENTE_PERMANENTE_RAIZES,
                    this.enums.procedimentoAtencaoBasica.RETRATAMENTO_ENDODÔNTICO_DENTE_PERMANENTE_UNIRADICULAR,
                    this.enums.procedimentoAtencaoBasica.SELAMENTO_PERFURACAO_RADICULAR,
                    this.enums.procedimentoAtencaoBasica.MOLDAGEM_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.REEMBASAMENTO_CONSERTO_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.CIMENTACAO_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.ADAPTACAO_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.AJUSTE_OCLUSAL,
                    this.enums.procedimentoAtencaoBasica.INSTALACAO_PROTESE_DENTARIA,
                    this.enums.procedimentoAtencaoBasica.APICECTOMIA,
                    this.enums.procedimentoAtencaoBasica.CURETAGEM_PERIAPICAL,
                    this.enums.procedimentoAtencaoBasica.EXODONTIA_DENTE_DECIDUO,
                    this.enums.procedimentoAtencaoBasica.EXODONTIA_DENTE_PERMANENTE,
                    this.enums.procedimentoAtencaoBasica.ODONTOSECCAO_RADILECTOMIA_TUNELIZACAO,
                    this.enums.procedimentoAtencaoBasica.REIMPLANTE_TRANSPLANTE_DENTAL,
                    this.enums.procedimentoAtencaoBasica.REMOCAO_DENTE_RETIDO,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_CIRURGICO_TRACIONAMENTO_DENTAL,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_ALVEOLITE,
                    this.enums.procedimentoAtencaoBasica.ULOTOMIA_ULECTOMIA,
                    this.enums.procedimentoAtencaoBasica.COROA_PROVISORIA,
                    this.enums.procedimentoAtencaoBasica.PROTESE_CORONARIAS,
                    this.enums.procedimentoAtencaoBasica.COROA_DE_ACO_E_POLICARBOXILATO,
                    this.enums.procedimentoAtencaoBasica.PROTESE_DENTARIA_SOBRE_IMPLANTE,
                ].includes(this.model.procedimento.codigo)) {
                    return 32
                }
                if([
                    this.enums.procedimentoAtencaoBasica.RASPAGEM_SUBGENGIVAIS,
                    this.enums.procedimentoAtencaoBasica.RASPAGEM_CORONO_RADICULAR,
                    this.enums.procedimentoAtencaoBasica.RASPAGEM_SUPRAGENGIVAIS,
                    this.enums.procedimentoAtencaoBasica.APROFUNDAMENTO_VESTIBULO_ORAL,
                    this.enums.procedimentoAtencaoBasica.GENGIVECTOMIA,
                    this.enums.procedimentoAtencaoBasica.EXODONTIA_MULTIPLA_ALVEOLOPLASTIA,
                    this.enums.procedimentoAtencaoBasica.GENGIVOPLASTIA,
                    this.enums.procedimentoAtencaoBasica.TRATAMENTO_CIRURGICO_PERIODONTAL,
                ].includes(this.model.procedimento.codigo)) {
                    return 6
                }
            }
            return 1
        },
    },
    mixins: [formats, object, repository],
    props: {
        data: {required: true, type: Object},
    },
    watch: {
        'model.material.material'(value) {
            this.form.possuiEntradaComLote = false
            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = false
            this.model.material.estoque = 0
            this.model.material.quantidade = null

            if (value && value.id) {
                this.loadEstoque()
            }
        },
        'model.material.quantidade': function () {
            if (this.form.possuiEntradaComLote && this.quantidadeValida) {
                this.selectEntradaItens()
            }
        },
    },
}
</script>